import type { Swiper as SwiperType } from "swiper";
import type { HomeIntroSlideData, HomeServiceItemData, HomeTrustedBrandItemData, IHandleGetFeaturedProductsParams } from "@model/Home.model";

import homeHero from "Utils/Assets/Images/home.webp";
import heroOfficeAutomation from "Utils/Assets/Images/office.webp";
import heroSystem from "Utils/Assets/Images/system-integration.webp";
import { IProductCardProps, ProductsResponse } from "@model/Products.model";
import { AxiosCalls } from "./Functions/AxiosCalls";
import { handleDefaultApiErrors } from "Utils/Global/apiErrorHandling";

export class HomeService {

    getHomeIntroSlides(): HomeIntroSlideData[] {
        return [
            {
                id: "home-1",
                imageUrl: homeHero,
                eyebrowLeft: "CHIMERATECH",
                eyebrowRight: "Smart home automation",
                title: "Control every space from a single screen.",
                subtitle:
                    "Showcase your premium automation panels, scenes, and experiences in the main hero of your website.",
                bottomTag: "Smart home experiences",
            },
            {
                id: "home-2",
                imageUrl: heroOfficeAutomation,
                eyebrowLeft: "CHIMERATECH",
                eyebrowRight: "Office automation",
                title: "Automate every workstation and meeting room.",
                subtitle:
                    "Control lighting, blinds, AC, projectors, and security from a unified automation platform.",
                bottomTag: "Office automation",
            },
            {
                id: "home-3",
                imageUrl: heroSystem,
                eyebrowLeft: "CHIMERATECH",
                eyebrowRight: "Lab connectivity",
                title: "Bring your labs and industry online.",
                subtitle:
                    "Connect machines, sensors, and spaces to deliver safer, smarter industrial experiences.",
                bottomTag: "Lab connectivity",
            },
        ];
    }

    navigateToSlideIndex(
        swiper: SwiperType | null,
        index: number,
        setActiveIndex: (idx: number) => void
    ): void {
        if (!swiper) return;
        swiper.slideToLoop(index);
        setActiveIndex(index);
    }
    getHomeServices(): HomeServiceItemData[] {
        return [
            {
                id: "home-automation",
                iconKind: "home",
                categoryLabel: "Home & building",
                title: "Home Automation",
                description:
                    "Design and deployment of intelligent lighting, climate, and security systems powered by KNX and leading automation brands. " +
                    "Tailored scenes and controls give every room a consistent, premium experience.",
                ctaLabel: "Learn more",
                ctaHref: "#home-automation",
            },
            {
                id: "software-dev",
                iconKind: "code",
                categoryLabel: "Custom apps",
                title: "Software Development",
                description:
                    "Cloud platforms, dashboards, and APIs that unify your devices, workflows, and data into a single control layer. " +
                    "From proof-of-concept to production, we design systems that scale with your business.",
                ctaLabel: "Learn more",
                ctaHref: "#software-development",
            },
            {
                id: "system-integration",
                iconKind: "power",
                categoryLabel: "Industry",
                title: "Seamless System Integration",
                description:
                    "Bridge your existing tools, services, and infrastructure into a unified ecosystem. " +
                    "Automate data flow, eliminate silos, and keep every part of your stack in sync.",
                ctaLabel: "Learn more",
                ctaHref: "#system-integration",
            },
        ];
    }


 async handleGetFeaturedProducts({
    limit,
    setLoading,
    setApiError,
    setItems,
  }: IHandleGetFeaturedProductsParams): Promise<void> {
    setLoading(true);
    setApiError(null);

    try {
      const query = new URLSearchParams();

      if (
        typeof limit === "number" &&
        Number.isFinite(limit) &&
        limit > 0
      ) {
        query.set("limit", String(limit));
      }

      const resp = await AxiosCalls.Get<ProductsResponse>(
        `/category/products/featured?${query.toString()}`
      );

      const data = resp?.data ?? {};
      const rawItems = Array.isArray(data.items) ? data.items : [];

      const normalized: IProductCardProps[] = rawItems.map((p: any) => {
        const priceNum = Number(p?.price ?? 0);

        const stockNum = Number.isFinite(Number(p?.stock))
          ? Number(p.stock)
          : 0;

        const maxPerUserRaw = Number(p?.maxPerUser);
        const maxPerUser =
          Number.isFinite(maxPerUserRaw) && maxPerUserRaw > 0
            ? maxPerUserRaw
            : undefined;

        const firstImageUrl =
          Array.isArray(p?.images) && p.images.length
            ? p.images[0]?.url ?? ""
            : "";

        const product: IProductCardProps = {
          id: String(p.id),
          name: String(p.name ?? ""),
          slug: String(p.slug ?? ""),
          price: priceNum,
          shortDescription: String(p.shortDescription ?? ""),
          imageUrl: firstImageUrl,
          stock: stockNum,
          maxPerUser,
        };

        return product;
      });

      setItems(normalized);
    } catch (err) {
      setApiError(handleDefaultApiErrors(err));
      setItems([]);
    }finally {
      setLoading(false);
    }
  }

    getTrustedBrands(): HomeTrustedBrandItemData[] {
        return [
            {
                id: "wago",
                name: "WAGO",
                logoUrl: "https://be.chimeratech.net/uploads/Wago-logo.webp",
            },
            {
                id: "schneider",
                name: "Schneider Electric",
                logoUrl: "https://be.chimeratech.net/uploads/Schneider-logo.webp",
            },
            {
                id: "mdt",
                name: "MDT",
                logoUrl: "https://be.chimeratech.net/uploads/MDT-logo.webp",
            },
            {
                id: "knx",
                name: "KNX",
                logoUrl: "https://be.chimeratech.net/uploads/KNX-logo.webp",
            },
        ];
    }

}
